import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  Loader2,
  Pencil,
  X,
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  ArrowLeft,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useUpdateAthlete } from "../../hooks/queries/useAthletes";
import {
  useAthleteCareer,
  useCreateAthleteCareer,
  useCreateAthletePreferences,
} from "../../hooks/queries/useAthleteData";
import AddAchievementModal from "./AddAchievementModal";
import TagSelector from "./TagSelector";
import {
  FOOTBALL_POSITIONS,
  FOOTBALL_SPORT_LABEL,
  LATERALITY_OPTIONS as LATERALITY_TAXONOMY,
  buildPositionFields,
  positionCodeFromText,
} from "../../constants/footballTaxonomy";

/* ───────────────────────────────────────────
   Constants
   ─────────────────────────────────────────── */
// Derivados de la taxonomía de fútbol compartida (Scout QA #24). El form sigue
// mostrando labels; al guardar se convierten a codes (position_code/group), que
// es lo que compara match_athletes_by_criteria.
const LATERALITY_OPTIONS = LATERALITY_TAXONOMY.map((l) => l.label);
const POSITION_OPTIONS = FOOTBALL_POSITIONS.map((p) => p.label);

const lateralityCodeFromLabel = (label) =>
  LATERALITY_TAXONOMY.find(
    (l) => l.label.toLowerCase() === String(label || "").toLowerCase(),
  )?.code || null;

const COMPETITION_LEVELS = [
  "Recreativo",
  "Amateur",
  "Semi-Profesional",
  "Profesional",
  "Élite / Selección",
];

const AGENCY_STATUSES = [
  "Sin representación",
  "Representado por agente",
  "Buscando agente",
  "Auto-representado",
];

const MAIN_OBJECTIVES = [
  "Conseguir beca deportiva",
  "Ser profesional",
  "Mejorar rendimiento",
  "Encontrar equipo",
  "Visibilidad internacional",
  "Otro",
];

const TOTAL_STEPS = 4;

/* ───────────────────────────────────────────
   Error Pop-up (reusable)
   ─────────────────────────────────────────── */
const ErrorPopup = ({ isOpen, message, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-8 relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Error</h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {message}
            </p>
            <button
              onClick={onClose}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ───────────────────────────────────────────
   Step Progress Bar
   ─────────────────────────────────────────── */
const StepProgressBar = ({ currentStep, totalSteps }) => (
  <div className="w-full flex gap-1 mb-8">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div
        key={i}
        className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${
          i <= currentStep ? "bg-blue-950" : "bg-gray-200"
        }`}
      />
    ))}
  </div>
);

/* ───────────────────────────────────────────
   Reusable Select Dropdown
   ─────────────────────────────────────────── */
const SelectDropdown = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || placeholder}
        </span>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-gray-700"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────────────────────────
   1–10 self-assessment scale
   ─────────────────────────────────────────── */
const RatingScale = ({ label, helper, value, onChange }) => (
  <div>
    <p className="text-sm text-gray-700 font-medium">{label}</p>
    <p className="text-xs text-gray-500 mb-3">{helper}</p>
    <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-pressed={String(value) === n}
          className={`h-11 rounded-lg border text-base font-semibold transition-colors ${
            String(value) === n
              ? "bg-blue-950 border-blue-950 text-white"
              : "bg-white border-gray-300 text-gray-700 hover:border-blue-900"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   InitialEvaluation — 4-step form
   ═══════════════════════════════════════════ */
const InitialEvaluation = ({ athleteId, onComplete }) => {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(null); // null = checking, true/false

  const { athleteData } = useAuth();
  const careerQuery = useAthleteCareer(athleteId);
  const updateAthlete = useUpdateAthlete();
  const createCareer = useCreateAthleteCareer();
  const createPreferences = useCreateAthletePreferences();

  // Check if the evaluation was already completed (career row exists). Only the
  // initial resolution counts: "Realizar de nuevo" flips the flag locally and a
  // background refetch must not re-lock the form.
  useEffect(() => {
    if (!athleteId) return;
    if (careerQuery.isFetched || careerQuery.isError) {
      setAlreadyCompleted((previous) =>
        previous === null ? Boolean(careerQuery.data) : previous,
      );
    }
  }, [athleteId, careerQuery.isFetched, careerQuery.isError, careerQuery.data]);

  /* ─── Step 1: Physical ─── */
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [laterality, setLaterality] = useState("");
  const [mainPosition, setMainPosition] = useState("");
  const [secondaryPosition, setSecondaryPosition] = useState("");
  const [tags, setTags] = useState([]);

  /* ─── Step 2: Career ─── */
  const [currentClub, setCurrentClub] = useState("");
  const [competitionLevel, setCompetitionLevel] = useState("");
  const [achievements, setAchievements] = useState([]);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  /* ─── Step 3: Performance ─── */
  const [speed, setSpeed] = useState("");
  const [endurance, setEndurance] = useState("");

  /* ─── Step 4: Preferences ─── */
  const [agencyStatus, setAgencyStatus] = useState("");
  const [travelAvailability, setTravelAvailability] = useState("");
  const [mainObjective, setMainObjective] = useState("");
  const [differentiator, setDifferentiator] = useState("");

  /* ─── Loading / Already-completed landing ─── */
  if (alreadyCompleted === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-blue-900" size={32} />
      </div>
    );
  }

  if (alreadyCompleted && step === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <motion.div
          className="bg-white rounded-2xl shadow-lg max-w-md w-full p-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={36} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            ¡Evaluación completada!
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            Ya completaste tu evaluación inicial. Si lo deseas, puedes volver a
            realizarla para actualizar tu información.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setAlreadyCompleted(false);
                setStep(0);
              }}
              className="w-full bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} />
              Realizar de nuevo
            </button>
            <button
              onClick={onComplete}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              Volver al panel
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  /* ─── Navigation ─── */
  const nextStep = () => {
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  /* ─── Achievement helpers ─── */
  const handleAddAchievement = (data) => {
    if (editingIndex !== null) {
      setAchievements((prev) => {
        const copy = [...prev];
        copy[editingIndex] = data;
        return copy;
      });
      setEditingIndex(null);
    } else {
      setAchievements((prev) => [...prev, data]);
    }
    setShowAchievementModal(false);
  };

  const handleRemoveAchievement = (idx) => {
    setAchievements((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleEditAchievement = (idx) => {
    setEditingIndex(idx);
    setShowAchievementModal(true);
  };

  /* ─── Final submit ─── */
  const handleSubmit = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Merge the evaluation data over the current attributes (the PATCH
      // replaces the whole attributes blob).
      const existingAttrs = athleteData?.attributes || {};

      // Los labels se guardan igual (UI y retrocompatibilidad); además se
      // derivan los codes estructurados, que son los que compara el matching
      // del scout. Un label que la taxonomía no reconoce deja el code en null y
      // el match cae al fallback por texto — degrada, no rompe.
      const mainPositionFields = buildPositionFields(
        positionCodeFromText(mainPosition),
      );

      const mergedAttributes = {
        ...existingAttrs,
        height: parseFloat(height) || 0,
        weight: parseFloat(weight) || 0,
        laterality,
        laterality_code: lateralityCodeFromLabel(laterality),
        main_position: mainPosition,
        secondary_position: secondaryPosition,
        position_code: mainPositionFields.position_code,
        position_group: mainPositionFields.position_group,
        secondary_position_code: positionCodeFromText(secondaryPosition),
        tags,
        sport: FOOTBALL_SPORT_LABEL,
        speed: parseFloat(speed) || 0,
        endurance: parseFloat(endurance) || 0,
      };

      // 1) Update the athlete row — Step 1/3 data
      await updateAthlete.mutateAsync({
        id: athleteId,
        position: mainPosition,
        // The column too, not just attributes.sport: scouts filter on the
        // column first, so an athlete who signed up under another sport would
        // otherwise stay hidden.
        sport: FOOTBALL_SPORT_LABEL,
        attributes: mergedAttributes,
      });

      // 2) Create the athlete_career row — Step 2 data (athlete_id is forced
      // server-side; only the field body is sent).
      const careerRow = {
        current_club: currentClub,
        competition_level: competitionLevel,
        achievements: achievements.map((a) => ({
          achievement: a.achievement,
          club: a.club,
          date: `${a.month} ${a.year}`,
          location: a.location,
        })),
      };

      await createCareer.mutateAsync(careerRow);

      // 3) Create the athlete_preferences row — Step 4 data
      const preferencesRow = {
        agency_status: agencyStatus,
        travel_availability: travelAvailability,
        main_objective: mainObjective,
        differentiator: differentiator,
      };

      await createPreferences.mutateAsync(preferencesRow);

      // Success — notify parent with the saved data for local state refresh.
      if (onComplete) {
        onComplete({
          position: mainPosition,
          attributes: mergedAttributes,
          athlete_career: [{ athlete_id: athleteId, ...careerRow }],
          athlete_preferences: [{ athlete_id: athleteId, ...preferencesRow }],
        });
      }
    } catch (err) {
      console.error("Error guardando evaluación:", err.message);
      setError(
        "Hubo un problema al guardar la evaluación. Inténtalo de nuevo.",
      );
      setShowErrorPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  /* ─── Render Steps ─── */
  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="number"
          placeholder="Estatura (cm)"
          value={height}
          onChange={(e) => setHeight(e.target.value)}
          className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900"
        />
        <input
          type="number"
          placeholder="Peso (kg)"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900"
        />
      </div>

      <SelectDropdown
        value={laterality}
        onChange={setLaterality}
        options={LATERALITY_OPTIONS}
        placeholder="Lateralidad"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectDropdown
          value={mainPosition}
          onChange={setMainPosition}
          options={POSITION_OPTIONS}
          placeholder="Posición Principal"
        />
        <SelectDropdown
          value={secondaryPosition}
          onChange={setSecondaryPosition}
          options={POSITION_OPTIONS}
          placeholder="Posición Secundaria"
        />
      </div>

      <div className="pt-2">
        <TagSelector value={tags} onChange={setTags} />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <input
        type="text"
        placeholder="Club/Equipo Actual"
        value={currentClub}
        onChange={(e) => setCurrentClub(e.target.value)}
        className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900"
      />

      <SelectDropdown
        value={competitionLevel}
        onChange={setCompetitionLevel}
        options={COMPETITION_LEVELS}
        placeholder="Nivel de competencia"
      />

      {/* Achievements section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Logros destacados ej: Campeón regional, Pichichi de la liga, etc.
        </p>
        <button
          type="button"
          onClick={() => {
            setEditingIndex(null);
            setShowAchievementModal(true);
          }}
          className="w-full sm:w-auto bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.15em] uppercase px-5 py-3 rounded-lg transition-colors whitespace-nowrap"
        >
          Añadir
        </button>
      </div>

      {/* Achievement cards */}
      {achievements.length > 0 && (
        <div className="space-y-3">
          {achievements.map((a, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <div className="w-3 h-3 mt-1 rounded-sm bg-blue-950 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900">
                  {a.achievement}
                </p>
                <p className="text-sm text-gray-600">{a.club}</p>
                <p className="text-xs text-gray-400">
                  {a.month} {a.year}
                </p>
                <p className="text-xs text-gray-400">
                  {a.location} - Presencial
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleEditAchievement(idx)}
                  className="text-gray-400 hover:text-blue-900 transition-colors"
                >
                  <Pencil size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveAchievement(idx)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <RatingScale
        label="Velocidad"
        helper="Autoevaluación del 1 (bajo) al 10 (élite)"
        value={speed}
        onChange={setSpeed}
      />
      <RatingScale
        label="Resistencia"
        helper="Autoevaluación del 1 (bajo) al 10 (élite)"
        value={endurance}
        onChange={setEndurance}
      />
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      <SelectDropdown
        value={agencyStatus}
        onChange={setAgencyStatus}
        options={AGENCY_STATUSES}
        placeholder="Estado de agencia"
      />

      {/* Travel availability */}
      <div>
        <p className="text-sm text-gray-700 font-medium mb-3">
          Disponibilidad para viajar
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-3 sm:gap-8">
          {[
            { label: "Sí", value: "si" },
            { label: "No", value: "no" },
            { label: "Solo becas", value: "solo_becas" },
          ].map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={travelAvailability === opt.value}
                onChange={() =>
                  setTravelAvailability(
                    travelAvailability === opt.value ? "" : opt.value,
                  )
                }
                className="w-5 h-5 rounded border-gray-300 text-blue-950 focus:ring-blue-900"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <SelectDropdown
        value={mainObjective}
        onChange={setMainObjective}
        options={MAIN_OBJECTIVES}
        placeholder="Objetivo principal"
      />

      <div>
        <p className="text-sm text-gray-700 font-medium mb-2">
          ¿Qué te hace diferente?
        </p>
        <textarea
          placeholder="máx. 140 caracteres"
          maxLength={140}
          value={differentiator}
          onChange={(e) => setDifferentiator(e.target.value)}
          className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 resize-none h-20"
        />
        <p className="text-right text-xs text-gray-400 mt-1">
          {differentiator.length}/140
        </p>
      </div>
    </div>
  );

  const steps = [renderStep1, renderStep2, renderStep3, renderStep4];
  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Progress bar */}
        <StepProgressBar currentStep={step} totalSteps={TOTAL_STEPS} />

        {/* Header */}
        <p className="text-sm text-gray-500 mb-1">
          Paso {step + 1} de {TOTAL_STEPS}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
          Evaluación inicial
        </h1>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            {steps[step]()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-10">
          {step > 0 ? (
            <button
              type="button"
              onClick={prevStep}
              className="bg-[#AEBFE4] hover:bg-[#9badd6] text-blue-950 text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200"
            >
              Anterior
            </button>
          ) : (
            <div />
          )}

          {isLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  Guardando
                  <Loader2 size={16} className="animate-spin" />
                </>
              ) : (
                <>
                  Siguiente
                  <span className="text-base">✦</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={nextStep}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
            >
              Siguiente
              <span className="text-base">✦</span>
            </button>
          )}
        </div>
      </div>

      {/* Achievement modal */}
      <AddAchievementModal
        isOpen={showAchievementModal}
        onClose={() => {
          setShowAchievementModal(false);
          setEditingIndex(null);
        }}
        onAdd={handleAddAchievement}
        isEditing={editingIndex !== null}
        initial={editingIndex !== null ? achievements[editingIndex] : null}
      />

      {/* Error popup */}
      <ErrorPopup
        isOpen={showErrorPopup}
        message={error}
        onClose={() => setShowErrorPopup(false)}
      />
    </div>
  );
};

export default InitialEvaluation;
